import type { FlowEdge, FlowNode, WorkflowLog } from '@lighthouse/shared'
import { WorkflowNodeFieldFormulaContext, WorkflowRunType, WorkflowType } from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import type { Updater } from 'use-immer'
import { useImmer } from 'use-immer'

type HelpLineHorizontalPosition = {
    top: number
    left: number
    width: number
}

type HelpLineVerticalPosition = {
    top: number
    left: number
    height: number
}

export type FlowModePayload = { mode: 'log'; logId: string } | { mode: 'editor' }

interface FlowProps {
    nodes: FlowNode[]
    edges: FlowEdge[]
    setNodes?: (payload: FlowNode[] | ((nodes: FlowNode[]) => FlowNode[])) => void
    setEdges?: (payload: FlowEdge[] | ((edges: FlowEdge[]) => FlowEdge[])) => void
}

export type FlowModalType =
    | 'NODE_CONFIGURE'
    | 'FLOW_RUNNING_START_TEST'
    | 'FLOW_RUNNING_RESULT'
    | 'NODE_RUNNING_RESULT'
    | 'FLOW_RUNNING_HISTORY'
    | 'FLOW_CONFIGURE'

interface FlowContextValue extends FlowProps {
    // helpLine
    helpLineHorizontal?: HelpLineHorizontalPosition
    helpLineVertical?: HelpLineVerticalPosition
    setHelpLineHorizontal?: (val?: HelpLineHorizontalPosition) => void
    setHelpLineVertical?: (val?: HelpLineVerticalPosition) => void
}

interface FlowProviderProps extends FlowProps {
    children: React.ReactNode
}

const FlowContext = React.createContext<FlowContextValue>({
    nodes: [],
    edges: []
})

type FlowState = {
    helpLineHorizontal?: HelpLineHorizontalPosition
    helpLineVertical?: HelpLineVerticalPosition
}

export const FlowProvider: React.FC<FlowProviderProps> = ({ children, nodes, edges, setNodes, setEdges }) => {
    const [state, setState] = useImmer<FlowState>({})

    const { helpLineHorizontal, helpLineVertical } = state

    const setHelpLineHorizontal = useCallback(
        (val?: HelpLineHorizontalPosition) => {
            setState(draft => {
                draft.helpLineHorizontal = val
            })
        },
        [setState]
    )

    const setHelpLineVertical = useCallback(
        (val?: HelpLineVerticalPosition) => {
            setState(draft => {
                draft.helpLineVertical = val
            })
        },
        [setState]
    )

    const contextValue = React.useMemo(
        () => ({
            helpLineHorizontal,
            helpLineVertical,
            nodes,
            edges,
            setNodes,
            setEdges,
            setHelpLineHorizontal,
            setHelpLineVertical
        }),
        [helpLineHorizontal, helpLineVertical, nodes, edges, setNodes, setEdges, setHelpLineHorizontal, setHelpLineVertical]
    )

    return <FlowContext.Provider value={contextValue}>{children}</FlowContext.Provider>
}

export const useFlow = () => {
    return React.useContext(FlowContext)
}

// container
export enum FlowDetailShape {
    'MODAL' = 'MODAL',
    'PAGE' = 'PAGE',
    'BOX' = 'BOX',
    'LOG_MODAL' = 'LOG_MODAL'
}

interface ModalState {
    type?: FlowModalType
    historyId?: string
    selectedNodeId?: string
}
interface FlowContainerContextValue {
    id: string
    type?: WorkflowType
    name: string
    scale?: number
    runType: WorkflowRunType
    errorMap?: Record<string, boolean>
    shape: FlowDetailShape
    isChanged: boolean
    enabled?: boolean
    log?: WorkflowLog
    disable: boolean
    closeConfirmOpen: boolean
    elRef?: React.RefObject<HTMLDivElement>
    setErrorMap?: (val: Record<string, boolean>) => void
    setIsChanged?: (val: boolean) => void
    setCloseConfirmOpen: (val: boolean) => void
    onClose?: () => void
    onFlowChange?: (val: FlowModePayload) => void
    modalState: ModalState
    setModalState: Updater<ModalState>
    setDisable: (val: boolean) => void
    requestClose: () => void
}

const FlowContainerContext = React.createContext<FlowContainerContextValue>({
    id: '',
    name: '',
    runType: WorkflowRunType.PARALLEL,
    shape: FlowDetailShape.PAGE,
    isChanged: false,
    disable: false,
    closeConfirmOpen: false,
    modalState: {},
    setModalState: () => void 0,
    setDisable: () => void 0,
    setCloseConfirmOpen: () => void 0,
    requestClose: () => void 0
})

interface FlowContainerProviderProps
    extends Omit<
        FlowContainerContextValue,
        | 'modalState'
        | 'setModalState'
        | 'isChanged'
        | 'setIsChanged'
        | 'disable'
        | 'setDisable'
        | 'closeConfirmOpen'
        | 'setCloseConfirmOpen'
        | 'requestClose'
    > {
    children: React.ReactNode
}
export const FlowContainerProvider: React.FC<FlowContainerProviderProps> = ({
    id,
    type,
    name,
    scale,
    runType,
    errorMap,
    shape,
    enabled,
    log,
    elRef,
    onClose,
    onFlowChange,
    children
}) => {
    const [nodeErrorMap, setErrorMap] = useState(errorMap)
    const [isChanged, setIsChanged] = useState(false)
    const [modalState, setModalState] = useImmer<ModalState>({})
    const [disable, setDisable] = useState(false)
    const [closeConfirmOpen, setCloseConfirmOpen] = useState(false)

    const requestClose = useCallback(() => {
        if (shape === FlowDetailShape.MODAL) {
            if (closeConfirmOpen) {
                return
            }

            if (isChanged) {
                setCloseConfirmOpen(true)
                return
            }
        }

        onClose?.()
    }, [closeConfirmOpen, isChanged, onClose, shape])

    const value = useMemo(
        () => ({
            id,
            type,
            name,
            scale,
            runType,
            errorMap: nodeErrorMap,
            shape,
            isChanged,
            enabled,
            log,
            elRef,
            disable,
            closeConfirmOpen,
            setDisable,
            setCloseConfirmOpen,
            setIsChanged,
            onClose,
            onFlowChange,
            setErrorMap,
            modalState,
            setModalState,
            requestClose
        }),
        [
            id,
            type,
            name,
            scale,
            runType,
            nodeErrorMap,
            shape,
            isChanged,
            enabled,
            log,
            elRef,
            disable,
            closeConfirmOpen,
            onClose,
            onFlowChange,
            modalState,
            setModalState,
            requestClose
        ]
    )
    const disableNodeFieldFormula = type === WorkflowType.AUTOMATION || type === WorkflowType.SUB_PROCESS
    return (
        <WorkflowNodeFieldFormulaContext.Provider value={disableNodeFieldFormula}>
            <FlowContainerContext.Provider value={value}>{children}</FlowContainerContext.Provider>
        </WorkflowNodeFieldFormulaContext.Provider>
    )
}

export const useFlowContainerContext = () => {
    return React.useContext(FlowContainerContext)
}
